import {move} from '@wandb/weave/common/util/data';
import {ID} from '@wandb/weave/common/util/id';
import {generateKeyBetween, generateNKeysBetween} from 'fractional-indexing';
import {castDraft, freeze, original, produce} from 'immer';
import _, {cloneDeep, findIndex, isEqual} from 'lodash';
import {getType} from 'typesafe-actions';

import {
  type PanelConfigOverride,
  type PanelPlacementOverride,
  SectionPanelSorting,
} from '../../components/PanelBank/types';
import type * as FilterTypes from '../../util/filterTypes';
import {
  FILTER_FORMAT_V2,
  type FilterV2Item,
  type FilterV2Root,
  type FlatIndividualItem,
  isFilterV2,
  isFilterV2Group,
  migrateLegacyFiltersToV2,
  withoutConnector,
} from '../../util/filterV2';
import {
  findNextPanelLoc,
  GRID_COLUMN_COUNT,
  GRID_ITEM_DEFAULT_HEIGHT,
  GRID_ITEM_DEFAULT_WIDTH,
} from '../../util/panelbankGrid';
import {getOverrideKey} from '../../util/panels';
import type {SortKey} from '../../util/queryTypes';
import {ReportSpecVersion} from '../../util/report';
import {keyFromString} from '../../util/runs';
import {type Key} from '../../util/runTypes';
import * as SM from '../../util/selectionmanager';
import {isSparseOverridesEnabled} from '../../util/sparseOverridesFeatureFlag';
import * as Actions from './actions';
import * as ActionsInternal from './actionsInternal';
import * as CustomRunColorsActions from './customRunColors/actions';
import * as DiscussionCommentActions from './discussionComment/actions';
import * as FilterActions from './filter/actions';
import * as GroupSelectionsActions from './groupSelections/actions';
import * as GroupSelectionsActionsInternal from './groupSelections/actionsInternal';
import * as MarkdownBlockActions from './markdownBlock/actions';
import {markSectionPanelsAutoImmer} from './markSectionPanelsAuto';
import * as Normalize from './normalize';
import {lookupPart, type StateType} from './normalizerSupport';
import * as PanelActions from './panel/actions';
import type * as PanelTypes from './panel/types';
import * as PanelUtils from './panel/utils';
import * as PanelBankConfigActions from './panelBankConfig/actions';
import * as PanelBankConfigActionsInternal from './panelBankConfig/actionsInternal';
import * as ConfigOverrideUtils from './panelBankConfig/configOverrideUtils';
import {type Ref as PanelBankConfigRef} from './panelBankConfig/types';
import * as PanelBankSectionConfigActions from './panelBankSectionConfig/actions';
import * as PanelBankSectionConfigActionsInternal from './panelBankSectionConfig/actionsInternal';
import type * as PanelBankSectionConfigTypes from './panelBankSectionConfig/types';
import {type PanelBankSectionConfigNormalized} from './panelBankSectionConfig/types';
import {movePanelAlphabeticallyInSection} from './panelBankSectionConfig/utils';
import * as PanelSettingsActions from './panelSettings/actions';
import {
  type ActionType,
  deleteParts,
  removeHistoryForObject,
  replacePart,
  type ViewReducerState,
} from './reducerSupport';
import * as ReportActions from './report/actions';
import * as RunSetActions from './runSet/actions';
import * as SectionActions from './section/actions';
import * as SortActions from './sort/actions';
import * as TempSelectionsActions from './tempSelections/actions';
import * as TempSelectionsActionsInternal from './tempSelections/actionsInternal';

/**
 * Build individual filter items from chart brush/lasso selections.
 * Shared by both legacy and V2 paths in selectionsToFilters.
 */
function buildSelectionFilters(
  selections: {[axis: string]: SM.PanelSelectionConstraint},
  axes?: string[]
): FilterTypes.IndividualFilter<Key>[] {
  const result: FilterTypes.IndividualFilter<Key>[] = [];
  // If axes aren't specified, create filters for all selections
  (axes || Object.keys(selections)).forEach(axis => {
    const axisSelect = selections[axis];
    const key = keyFromString(axis);
    ['high', 'low'].forEach(boundary => {
      const value = axisSelect[boundary as 'high' | 'low'];
      if (key != null && value != null) {
        result.push({
          key,
          op: boundary === 'low' ? '>=' : '<=',
          value,
        });
      }
    });
    // Categorical variables (string/boolean columns)
    if (
      key != null &&
      axisSelect.match != null &&
      axisSelect.match.length > 0
    ) {
      result.push({key, op: 'IN', value: axisSelect.match});
    }
  });
  return result;
}

/**
 * Apply selection filters to a FilterV2Root by removing existing
 * duplicates (same key+op) and appending new ones with AND connectors.
 */
function applySelectionsToV2(
  root: FilterV2Root,
  newFilters: FlatIndividualItem[]
): FilterV2Root {
  const kept = root.filters.filter(item => {
    if (isFilterV2Group(item)) {
      return true;
    }
    return !newFilters.some(s => isEqual(s.key, item.key) && s.op === item.op);
  });
  const newItems: FilterV2Item[] = newFilters.map(s => ({
    ...s,
    connector: 'AND',
  }));
  const combined = [...kept, ...newItems];
  if (combined.length > 0) {
    combined[0] = withoutConnector(combined[0]);
  }
  return {filterFormat: FILTER_FORMAT_V2, filters: combined};
}

/**
 * Internal use, but must be implemented for all undoable actions.
 * Should apply action, and return an inverse action (an action that
 * does the original action). The inverse action must be inversible
 * itself.
 *
 * This function is specifically for actions being processed with immer.
 * Immer is a known souce of perf issues when modifying large state objects.
 * If you encounter actions performing poorly due to immer, please refactor
 * to use the non-immer handler.
 *
 * Warning: 'state' here is actually an immer draft, which is updated with mutations.
 *    Sometimes you'll need a copy of the previous state value before mutating it.
 *    If so, use immer's `original` to read from the original, immutable state object.
 *    Avoid `cloneDeep` if possible, as it can be very expensive in large workspace.
 *    You can also use `original` to fix the "cannot perform 'get' on revoked proxy"
 *    errors that sometimes pop up if you're using the redux devtools.
 **/
export function applyAndMakeInverseActionImmerInner(
  state: ViewReducerState,
  action: ActionType
): ActionType {
  switch (action.type) {
    case getType(Actions.noop): {
      return Actions.noop();
    }
    case getType(SectionActions.removeRunSet): {
      const {ref, runSetRef} = action.payload;
      const section = state.parts.section[ref.id];
      const index = findIndex(
        section.runSetRefs,
        r => r.viewID === runSetRef.viewID && r.id === runSetRef.id
      );
      if (index === -1) {
        throw new Error('invalid action');
      }
      section.runSetRefs.splice(index, 1);
      if (
        section.openRunSet != null &&
        section.openRunSet >= section.runSetRefs.length
      ) {
        section.openRunSet =
          section.openRunSet === 0 ? undefined : section.runSetRefs.length - 1;
      }
      const runSet = Normalize.denormalize(state.parts, runSetRef);
      removeHistoryForObject(state, runSetRef);
      deleteParts(state, runSetRef);
      return SectionActions.insertRunSet(ref, index, runSet);
    }
    case getType(SectionActions.duplicateRunSet): {
      const {ref, runSetRef} = action.payload;
      const section = state.parts.section[ref.id];
      const sourceRunSet = Normalize.denormalize(state.parts, runSetRef);
      const partRef = Normalize.addObj(state.parts, 'runSet', ref.viewID, {
        ...sourceRunSet,
        id: ID(9),
        name: `Run set ${section.runSetRefs.length + 1}`,
      });
      section.runSetRefs.push(partRef);
      section.openRunSet = section.runSetRefs.length - 1;
      return SectionActions.removeRunSet(ref, partRef);
    }
    case getType(SectionActions.reorderRunSet): {
      const {ref, indexFrom, indexTo} = action.payload;

      const section = state.parts.section[ref.id];

      const openID =
        section.openRunSet != null && section.runSetRefs[section.openRunSet].id;

      section.runSetRefs = move(section.runSetRefs, indexFrom, indexTo);
      // If there is an open section maintain it.
      if (section.openRunSet != null) {
        const idx = section.runSetRefs.findIndex(
          runSetRef => runSetRef.id === openID
        );
        section.openRunSet = idx;
      }

      return SectionActions.reorderRunSet(ref, indexTo, indexFrom);
    }
    case getType(SectionActions.insertRunSet): {
      const {ref, runSet, index} = action.payload;
      const section = state.parts.section[ref.id];

      const runSetRef = Normalize.addObj(
        state.parts,
        'runSet',
        ref.viewID,
        runSet
      );

      section.runSetRefs.splice(index, 0, runSetRef);
      return SectionActions.removeRunSet(ref, runSetRef);
    }
    case getType(SectionActions.setActiveIndex): {
      const {ref, index} = action.payload;
      const section = state.parts.section[ref.id];
      const prevIndex = section.openRunSet;
      section.openRunSet = index;
      return SectionActions.setActiveIndex(ref, prevIndex);
    }
    case getType(SectionActions.setHideRunSets): {
      const {ref, hide} = action.payload;
      const section = state.parts.section[ref.id];
      const prevHide = section.hideRunSets ?? false;
      section.hideRunSets = hide;
      return SectionActions.setHideRunSets(ref, prevHide);
    }
    case getType(SectionActions.setOpen): {
      const {ref, open} = action.payload;
      const section = state.parts.section[ref.id];
      const prevOpen = section.openViz || false;
      section.openViz = open;
      return SectionActions.setOpen(ref, prevOpen);
    }
    case getType(SectionActions.setName): {
      const {ref, name} = action.payload;
      const section = state.parts.section[ref.id];
      const prevName = section.name || '';
      section.name = name;
      return SectionActions.setName(ref, prevName);
    }
    case getType(MarkdownBlockActions.setContent): {
      const {ref, content} = action.payload;
      const block = state.parts['markdown-block'][ref.id];
      if (block == null) {
        return Actions.noop();
      }
      const prevContent = block.content;
      block.content = content;
      return MarkdownBlockActions.setContent(ref, prevContent);
    }
    case getType(MarkdownBlockActions.setCollapsed): {
      const {ref, collapsed} = action.payload;
      const block = state.parts['markdown-block'][ref.id];
      const prevCollapsed = !!block.collapsed;
      block.collapsed = collapsed;
      return MarkdownBlockActions.setCollapsed(ref, prevCollapsed);
    }
    case getType(PanelSettingsActions.set): {
      const {ref, panelSettings} = action.payload;
      const prev = original(lookupPart(state.parts, ref))!;
      state.parts.panelSettings[ref.id] = panelSettings;
      return PanelSettingsActions.set(ref, prev);
    }
    case getType(PanelSettingsActions.update): {
      const {ref, panelSettingsUpdate} = action.payload;
      const prev = original(lookupPart(state.parts, ref))!;
      Object.assign(state.parts.panelSettings[ref.id], panelSettingsUpdate);
      return PanelSettingsActions.set(ref, prev);
    }
    case getType(RunSetActions.set): {
      const {ref, runSetNorm} = action.payload;
      const prev = original(lookupPart(state.parts, ref))!;
      state.parts.runSet[ref.id] = runSetNorm;
      return RunSetActions.set(ref, prev);
    }
    case getType(SortActions.set): {
      const {ref, sort} = action.payload;
      const prev = original(lookupPart(state.parts, ref))!;
      state.parts.sort[ref.id] = sort;
      return SortActions.set(ref, prev);
    }
    case getType(SortActions.upsert): {
      const {ref, sortKey} = action.payload;
      const sort = lookupPart(state.parts, ref);

      if (sort.keys.length <= 1) {
        // Single-key or zero-key sort: just replace it.
        sort.keys = [sortKey];
      } else {
        // Multi-key sort: update this column's entry in place, or append it.
        const existing = sort.keys.find((sk: SortKey) =>
          isEqual(sk.key, sortKey.key)
        );
        if (existing) {
          existing.ascending = sortKey.ascending;
        } else {
          sort.keys.push(sortKey);
        }
      }

      return SortActions.set(action.payload.ref, original(sort)!);
    }
    case getType(FilterActions.selectionsToFilters): {
      const {ref, selections, axes} = action.payload;
      const prevFilters = state.parts.filters[ref.id];
      const newFilters = buildSelectionFilters(selections, axes);

      if (newFilters.length === 0) {
        return FilterActions.set(ref, prevFilters);
      }

      // Stored filters in Redux are FilterV2 after migrate-on-read; the
      // migration call here is a defensive no-op for typical state but
      // keeps things correct if a legacy filter slips through.
      const v2 = isFilterV2(prevFilters)
        ? prevFilters
        : migrateLegacyFiltersToV2(prevFilters);
      state.parts.filters[ref.id] = applySelectionsToV2(
        cloneDeep(v2),
        newFilters
      );
      return FilterActions.set(ref, prevFilters);
    }
    case getType(CustomRunColorsActions.setCustomRunColor): {
      const {ref, id, color} = action.payload;
      const prevColor = lookupPart(state.parts, ref)[id];
      /**
       * This allows us to delete a custom color by setting the color to null
       */
      if (color === null) {
        delete lookupPart(state.parts, ref)[id];
      } else {
        lookupPart(state.parts, ref)[id] = color;
      }
      return CustomRunColorsActions.setCustomRunColor(ref, id, prevColor);
    }
    case getType(CustomRunColorsActions.setCustomRunColors): {
      const {ref, colors} = action.payload;
      const customRunColors = lookupPart(state.parts, ref);
      const prevColors: Record<string, string | null> = {};

      Object.entries(colors).forEach(([id, color]) => {
        prevColors[id] = customRunColors[id] ?? null;
        if (color === null) {
          delete customRunColors[id];
        } else {
          customRunColors[id] = color;
        }
      });

      return CustomRunColorsActions.setCustomRunColors(ref, prevColors);
    }
    case getType(GroupSelectionsActions.toggleExpandedRowAddress): {
      const ref = action.payload.ref;
      const prevGroupSelections = original(
        castDraft(lookupPart(state.parts, ref))
      )!;
      const newGroupSelections = SM.toggleExpandedRowAddress(
        prevGroupSelections,
        action.payload.rowAddress
      );
      state.parts[ref.type][ref.id] = castDraft(newGroupSelections);
      return GroupSelectionsActionsInternal.setGroupSelections(
        ref,
        prevGroupSelections
      );
    }
    case getType(GroupSelectionsActions.addBound): {
      const {ref, bound} = action.payload;
      const prevGroupSelections = original(
        castDraft(lookupPart(state.parts, ref))
      )!;
      const newGroupSelections = SM.addBound(prevGroupSelections, bound);
      state.parts[ref.type][ref.id] = castDraft(newGroupSelections);
      return GroupSelectionsActionsInternal.setGroupSelections(
        ref,
        prevGroupSelections
      );
    }
    case getType(GroupSelectionsActions.selectAll): {
      const ref = action.payload.ref;
      const prevGroupSelections = original(
        castDraft(lookupPart(state.parts, ref))
      )!;
      const newGroupSelections = SM.selectAll(prevGroupSelections);
      state.parts[ref.type][ref.id] = castDraft(newGroupSelections);
      // If the user has chosen to visualize all, ensure the runSet is enabled.
      // Iterate through all runsets to find the one that contains this groupSelection
      // ref.
      for (const runSet of Object.values(state.parts.runSet)) {
        if (isEqual(runSet.groupSelectionsRef, ref)) {
          runSet.enabled = true;
        }
      }
      return GroupSelectionsActionsInternal.setGroupSelections(
        ref,
        prevGroupSelections
      );
    }
    case getType(GroupSelectionsActions.selectNone): {
      const ref = action.payload.ref;
      const prevGroupSelections = original(
        castDraft(lookupPart(state.parts, ref))
      )!;
      const newGroupSelections = SM.selectNone(prevGroupSelections);
      state.parts[ref.type][ref.id] = castDraft(newGroupSelections);
      return GroupSelectionsActionsInternal.setGroupSelections(
        ref,
        prevGroupSelections
      );
    }
    case getType(GroupSelectionsActionsInternal.setGroupSelections): {
      const ref = action.payload.ref;
      const prevGroupSelections = original(
        castDraft(lookupPart(state.parts, ref))
      )!;
      state.parts[ref.type][ref.id] = castDraft(action.payload.groupSelections);
      return GroupSelectionsActionsInternal.setGroupSelections(
        ref,
        prevGroupSelections
      );
    }
    case getType(TempSelectionsActionsInternal.setTempSelections): {
      const {ref, tempSelections} = action.payload;
      const prevTempSelections = original(
        castDraft(lookupPart(state.parts, ref))
      )!;

      state.parts[ref.type][ref.id] = castDraft(tempSelections);

      return TempSelectionsActionsInternal.setTempSelections(
        ref,
        prevTempSelections
      );
    }
    case getType(PanelActions.setConfigs): {
      const {refs, configs} = action.payload;
      const prevPanelConfigs = refs.map(ref =>
        original(lookupPart(state.parts, ref).config)!
      );
      for (let i = 0; i < refs.length; i++) {
        lookupPart(state.parts, refs[i]).config = configs[i];
      }
      return PanelActions.setConfigs(refs, prevPanelConfigs);
    }
    case getType(PanelActions.updateConfigs): {
      const {refs, configUpdate} = action.payload;
      const prevPanelConfigs = refs.map(ref =>
        original(lookupPart(state.parts, ref).config)!
      );
      for (const ref of refs) {
        Object.assign(lookupPart(state.parts, ref).config, configUpdate);
      }
      return PanelActions.setConfigs(refs, prevPanelConfigs);
    }
    case getType(PanelBankConfigActions.addSection): {
      // sectionRef is the existing section that you're inserting before or after
      const {ref, sectionRef, options} = action.payload;
      const newSectionRef =
        PanelBankConfigActionsInternal.addPanelBankSectionInternal(
          state,
          ref,
          sectionRef,
          options || {}
        );
      return PanelBankConfigActions.deleteSection(ref, newSectionRef);
    }
    case getType(PanelBankConfigActions.deleteSection): {
      // sectionRef is the section you're deleting
      const {ref, sectionRef, workspaceSettingsRef} = action.payload;
      const undoAction =
        PanelBankConfigActionsInternal.deletePanelBankSectionInternal(
          state,
          ref,
          sectionRef,
          workspaceSettingsRef
        );
      return undoAction;
    }
    case getType(PanelBankConfigActionsInternal.putSection): {
      // This internal action is the inverse of deleteSection.
      const {
        ref,
        sectionRef,
        sectionNorm,
        prevIndex,
        deletePanelsResult,
        workspaceSettingsRef,
      } = action.payload;
      // Add section ref back to config.
      lookupPart(state.parts, ref).sectionRefs.splice(prevIndex, 0, sectionRef);
      // Put normalized section back in parts.
      state.parts[sectionRef.type][sectionRef.id] = sectionNorm;
      // Put removed panels back
      PanelUtils.undoDeletePanels(state, deletePanelsResult, ref);
      return PanelBankConfigActions.deleteSection(
        ref,
        sectionRef,
        workspaceSettingsRef
      );
    }
    case getType(PanelBankConfigActions.pinSection): {
      const {ref, sectionRef} = action.payload;

      const prev = original(lookupPart(state.parts, ref).sectionRefs)!;

      const isPinned = lookupPart(state.parts, sectionRef).pinned;

      const next: PanelBankSectionConfigTypes.Ref[] = [];

      // all pinned sections excluding the one we're pinning
      const pinnedWithoutCurrRef = prev.filter(sRef => {
        const section = lookupPart(state.parts, sRef);
        return section.pinned && sRef.id !== sectionRef.id;
      });

      // all unpinned sections excluding the one we're pinning
      const unPinnedWithoutCurrRef = prev.filter(sRef => {
        const section = lookupPart(state.parts, sRef);
        return !section.pinned && sRef.id !== sectionRef.id;
      });

      /*
       * if the section is getting unpinned, it should be placed after the last pinned section
       * if the section is getting pinned, it should be placed before the first unpinned section
       */
      if (!isPinned) {
        next.push(sectionRef);
        next.push(...pinnedWithoutCurrRef);
      } else {
        next.push(...pinnedWithoutCurrRef);
        next.push(sectionRef);
      }

      next.push(...unPinnedWithoutCurrRef);

      lookupPart(state.parts, ref).sectionRefs = next;

      lookupPart(state.parts, sectionRef).pinned = !isPinned;

      return PanelBankConfigActions.pinSection(ref, sectionRef);
    }
    case getType(PanelBankConfigActions.updateSettingsAndSortPanels): {
      const {
        ref,
        args: {sortAlphabetically, workspaceSettingsRef},
      } = action.payload;
      // update settings
      const prevSortAlphabetically = lookupPart(
        state.parts,
        workspaceSettingsRef
      ).sortAlphabetically;
      Object.assign(lookupPart(state.parts, workspaceSettingsRef), {
        sortAlphabetically,
      });

      // Clear placement overrides when enabling alphabetical sort — the user
      // is explicitly choosing a new ordering. The undo action captures
      // prevOverrides and restores them if the user undoes the sort.
      const prevOverrides = original(
        lookupPart(state.parts, ref)
      )?.panelPlacementOverrides;
      if (sortAlphabetically) {
        lookupPart(state.parts, ref).panelPlacementOverrides = undefined;
      }

      const sectionRefs = original(lookupPart(state.parts, ref).sectionRefs)!;
      const sectionRefsToSort = sortAlphabetically
        ? sectionRefs
        : sectionRefs.filter(
            sectionRef =>
              lookupPart(state.parts, sectionRef).sorted !==
              SectionPanelSorting.Manual
          );
      // sort panels
      const prevPanelRefs: PanelTypes.Ref[][] = [];
      sectionRefsToSort.forEach(sectionRef => {
        const panelRefs = lookupPart(state.parts, sectionRef).panelRefs;
        prevPanelRefs.push(cloneDeep(panelRefs));
        PanelBankSectionConfigActionsInternal.sortPanelsInternal(
          state,
          sectionRef,
          panelRefs
        );
      });
      return PanelBankConfigActionsInternal.undoUpdateSettingsAndSortPanels(
        ref,
        {
          workspaceSettingsRef,
          sortAlphabetically: prevSortAlphabetically,
          sectionRefs: sectionRefsToSort,
          panelRefs: prevPanelRefs,
          prevOverrides,
        }
      );
    }
    case getType(
      PanelBankConfigActionsInternal.undoUpdateSettingsAndSortPanels
    ): {
      const {
        ref,
        args: {
          sortAlphabetically,
          workspaceSettingsRef,
          sectionRefs,
          panelRefs,
          prevOverrides,
        },
      } = action.payload;
      // update settings
      const prevSortAlphabetically = lookupPart(
        state.parts,
        workspaceSettingsRef
      ).sortAlphabetically;
      Object.assign(lookupPart(state.parts, workspaceSettingsRef), {
        sortAlphabetically,
      });

      // Restore panel placement overrides
      const currentOverrides = original(
        lookupPart(state.parts, ref)
      )?.panelPlacementOverrides;
      lookupPart(state.parts, ref).panelPlacementOverrides = prevOverrides;

      const prevPanelRefs: PanelTypes.Ref[][] = [];
      sectionRefs.forEach((sectionRef, index) => {
        prevPanelRefs.push(
          cloneDeep(lookupPart(state.parts, sectionRef).panelRefs)
        );
        lookupPart(state.parts, sectionRef).panelRefs = panelRefs[index];
      });

      return PanelBankConfigActionsInternal.undoUpdateSettingsAndSortPanels(
        ref,
        {
          workspaceSettingsRef,
          sortAlphabetically: prevSortAlphabetically,
          sectionRefs,
          panelRefs: prevPanelRefs,
          prevOverrides: currentOverrides,
        }
      );
    }
    // Only used for development (to stub undo actions)
    case getType(PanelBankConfigActions.noOp): {
      const {ref} = action.payload;
      return PanelBankConfigActions.noOp(ref);
    }
    case getType(PanelActions.deletePanels): {
      const {
        panelRefs,
        sectionRef,
        panelBankConfigRef,
        workspaceSettingsRef,
        isPanelsAutoForUndoRedo,
      } = action.payload;
      const deletePanelsResult = PanelUtils.deletePanels(
        state,
        panelRefs,
        sectionRef,
        panelBankConfigRef,
        workspaceSettingsRef
      );
      return PanelActions.undoDeletePanels(
        deletePanelsResult,
        sectionRef,
        panelBankConfigRef,
        workspaceSettingsRef,
        isPanelsAutoForUndoRedo
      );
    }
    case getType(PanelActions.undoDeletePanels): {
      const {
        deletePanelsResult,
        sectionRef,
        panelBankConfigRef,
        workspaceSettingsRef,
        isPanelsAutoForRedo,
      } = action.payload;
      const restoredPanelRefs = PanelUtils.undoDeletePanels(
        state,
        deletePanelsResult,
        panelBankConfigRef
      );
      return PanelActions.deletePanels(
        restoredPanelRefs,
        sectionRef,
        panelBankConfigRef,
        workspaceSettingsRef,
        isPanelsAutoForRedo ?? {value: false}
      );
    }
    case getType(PanelBankSectionConfigActions.duplicatePanel): {
      const {ref, panelRef, panelBankConfigRef} = action.payload;
      const prevIsPanelsAuto = lookupPart(state.parts, ref).isPanelsAuto;
      const panel = Normalize.denormalize(state.parts, panelRef);
      const panelIndex = lookupPart(state.parts, ref).panelRefs.findIndex(
        pRef => pRef.id === panelRef.id
      );
      const newId = ID();
      const clonedPanel = {
        ...cloneDeep(panel),
        __id__: newId,
        // When sparse overrides are enabled, mark the duplicate as custom
        // so it gets a unique override key (__custom_panel__:${newId})
        // instead of sharing the original's metric-based key (collision).
        ...(isSparseOverridesEnabled() ? {isAuto: false} : {}),
      };
      const newPanelRef = Normalize.addObj(
        state.parts,
        'panel',
        ref.viewID,
        clonedPanel
      );
      if (isSparseOverridesEnabled() && panelBankConfigRef) {
        // Append to the end of panelRefs to avoid shifting positional keys
        // of existing panels. The placement override positions it visually.
        lookupPart(state.parts, ref).panelRefs.push(newPanelRef);

        const overrideKey = getOverrideKey(clonedPanel);
        if (overrideKey) {
          const sectionDraft = lookupPart(state.parts, ref);
          const sectionId = sectionDraft.__id__;
          const pbcDraft = lookupPart(state.parts, panelBankConfigRef);
          const existingOverrides = pbcDraft.panelPlacementOverrides ?? {};

          // Place the duplicate just before the original in display order.
          // Build ALL effective keys (positional + overrides) for the section,
          // find the original's effective key and the key just before it.
          // This handles all cases uniformly: original with/without override,
          // and multiple duplicates of the same panel (each gets a unique key
          // because it slots between the last duplicate and the original).
          const originalPanel = state.parts[panelRef.type][panelRef.id];
          const originalKey = getOverrideKey(originalPanel);
          const originalOverride =
            originalKey != null ? existingOverrides[originalKey] : undefined;
          const originalHasOverride =
            originalOverride != null &&
            originalOverride.sectionId === sectionId;

          let nonOverriddenCount = 0;
          let positionalRankAtOriginal: number | null = null;
          const allEffectiveKeys: string[] = [];
          for (let i = 0; i < sectionDraft.panelRefs.length; i++) {
            const pRef = sectionDraft.panelRefs[i];
            if (pRef.id === newPanelRef.id) {
              continue; // skip the duplicate we just pushed
            }
            const p = state.parts[pRef.type][pRef.id];
            const pk = getOverrideKey(p);
            const hasOverride =
              pk != null &&
              pk in existingOverrides &&
              existingOverrides[pk].sectionId === sectionId;
            if (hasOverride) {
              allEffectiveKeys.push(existingOverrides[pk].orderKey);
            } else {
              if (i === panelIndex) {
                positionalRankAtOriginal = nonOverriddenCount;
              }
              nonOverriddenCount++;
            }
          }

          const positionalKeys = generateNKeysBetween(
            null,
            null,
            nonOverriddenCount || 1
          );
          allEffectiveKeys.push(...positionalKeys);
          allEffectiveKeys.sort();

          // The original's effective key: override orderKey if it has one,
          // otherwise its positional key.
          const originalEffectiveKey = originalHasOverride
            ? originalOverride.orderKey
            : positionalRankAtOriginal != null
              ? positionalKeys[positionalRankAtOriginal]
              : null;

          // Find the key that sorts immediately before the original
          let beforeOriginalKey: string | null = null;
          if (originalEffectiveKey != null) {
            for (const ek of allEffectiveKeys) {
              if (ek >= originalEffectiveKey) {
                break;
              }
              beforeOriginalKey = ek;
            }
          }

          const orderKey = generateKeyBetween(
            beforeOriginalKey,
            originalEffectiveKey
          );
          pbcDraft.panelPlacementOverrides = {
            ...existingOverrides,
            [overrideKey]: {sectionId, orderKey},
          };
        }
      } else {
        // Legacy path: insert at original position
        lookupPart(state.parts, ref).panelRefs.splice(
          panelIndex,
          0,
          newPanelRef
        );
      }

      return PanelBankSectionConfigActions.deletePanel(
        ref,
        newPanelRef,
        panelBankConfigRef,
        undefined,
        {value: prevIsPanelsAuto}
      );
    }
    case getType(PanelBankSectionConfigActions.deletePanel): {
      const {
        ref,
        panelRef,
        panelBankConfigRef,
        workspaceSettingsRef,
        isPanelsAutoForUndoRedo,
      } = action.payload;
      const deletePanelsResult = PanelUtils.deletePanels(
        state,
        [panelRef],
        ref,
        panelBankConfigRef,
        workspaceSettingsRef
      );
      return PanelActions.undoDeletePanels(
        deletePanelsResult,
        ref,
        panelBankConfigRef,
        workspaceSettingsRef,
        isPanelsAutoForUndoRedo
      );
    }
    // Create a new section and move a panel to it in one shot
    case getType(PanelBankConfigActions.movePanelToNewSection): {
      const {ref, args} = action.payload;
      const {fromSectionRef, panelRef, newSectionName} = args;

      const newSectionRef =
        PanelBankConfigActionsInternal.addPanelBankSectionInternal(
          state,
          ref,
          fromSectionRef,
          {newSectionName}
        );
      const {
        fromIndex,
        prevIsPanelAuto,
        prevIsFromSectionPanelsAuto,
        prevIsToSectionPanelsAuto,
      } = PanelBankConfigActionsInternal.movePanelInternal(state, {
        ref,
        panelRef,
        fromSectionRef,
        toSectionRef: newSectionRef,
        toIndex: 0,
        isPanelAuto: false, // panel becomes not auto if moved to a new section
      });

      return PanelBankConfigActionsInternal.undoMovePanelImmer(
        ref,
        panelRef,
        newSectionRef,
        fromSectionRef,
        fromIndex,
        prevIsPanelAuto,
        prevIsToSectionPanelsAuto,
        prevIsFromSectionPanelsAuto,
        newSectionName
      );
    }

    case getType(PanelBankConfigActionsInternal.undoMovePanelImmer): {
      const {
        ref,
        panelRef,
        fromSectionRef,
        toSectionRef,
        toIndex,
        newSectionName,
        isPanelAuto,
        isFromSectionPanelsAuto,
        isToSectionPanelsAuto,
      } = action.payload;

      const {fromIndex} = PanelBankConfigActionsInternal.movePanelInternal(
        state,
        {
          ref,
          panelRef,
          fromSectionRef,
          toSectionRef,
          toIndex,
          isPanelAuto,
        }
      );

      // revert section.isPanelsAuto flags
      lookupPart(state.parts, fromSectionRef).isPanelsAuto =
        isFromSectionPanelsAuto;
      lookupPart(state.parts, toSectionRef).isPanelsAuto =
        isToSectionPanelsAuto;

      if (newSectionName !== undefined) {
        PanelBankConfigActionsInternal.deletePanelBankSectionInternal(
          state,
          ref,
          fromSectionRef
        );
        return PanelBankConfigActions.movePanelToNewSection(ref, {
          panelRef,
          fromSectionRef: toSectionRef,
          newSectionName,
        });
      }

      return PanelBankConfigActions.movePanelImmer(
        ref,
        panelRef,
        toSectionRef,
        fromSectionRef,
        fromIndex
      );
    }
    case getType(PanelBankConfigActions.movePanelImmer): {
      const {ref, panelRef, fromSectionRef, toSectionRef, inactivePanelRefIDs} =
        action.payload;
      // If toIndex is not specified, add panel to the end of the list
      const toIndex =
        action.payload.toIndex == null
          ? lookupPart(state.parts, toSectionRef).panelRefs.length
          : action.payload.toIndex;

      const {
        fromIndex,
        prevIsPanelAuto,
        prevIsFromSectionPanelsAuto,
        prevIsToSectionPanelsAuto,
      } = PanelBankConfigActionsInternal.movePanelInternal(state, {
        ref,
        fromSectionRef,
        panelRef,
        toSectionRef,
        toIndex,
        inactivePanelRefIDs,
        isPanelAuto: false, // panel becomes not auto if moved to another section
      });

      return PanelBankConfigActionsInternal.undoMovePanelImmer(
        ref,
        panelRef,
        toSectionRef,
        fromSectionRef,
        fromIndex,
        prevIsPanelAuto,
        prevIsToSectionPanelsAuto,
        prevIsFromSectionPanelsAuto
      );
    }
    case getType(PanelBankSectionConfigActions.toggleType): {
      const {ref} = action.payload;
      const panelBankSectionConfig = lookupPart(state.parts, ref);
      const panelRefs = panelBankSectionConfig.panelRefs;
      panelBankSectionConfig.type =
        panelBankSectionConfig.type === 'grid' ? 'flow' : 'grid';
      // If we're switching to grid type, add layout to any panels that don't already have it
      if (panelBankSectionConfig.type === 'grid') {
        const panels = Normalize.denormalize(state.parts, ref).panels;
        panels.forEach((panel, i) => {
          if (panel.layout == null) {
            const panelRef = panelRefs[i];

            lookupPart(state.parts, panelRef).layout = {
              ...findNextPanelLoc(
                Normalize.denormalize(state.parts, ref)
                  .panels.map(p => p.layout)
                  .filter(l => l),
                GRID_COLUMN_COUNT,
                GRID_ITEM_DEFAULT_WIDTH
              ),
              w: GRID_ITEM_DEFAULT_WIDTH,
              h: GRID_ITEM_DEFAULT_HEIGHT,
            };
          }
        });
      }
      return PanelBankSectionConfigActions.toggleType(ref);
    }
    case getType(PanelBankSectionConfigActions.setGridLayout): {
      const {ref, newGridLayout} = action.payload;
      const prevGridLayout = Normalize.denormalize(state.parts, ref).panels.map(
        (p, i) => ({
          ...p.layout,
          id: lookupPart(state.parts, ref).panelRefs[i].id,
        })
      );
      const panelRefs = lookupPart(state.parts, ref).panelRefs;
      panelRefs.forEach(panelRef => {
        const newLayoutIndex = newGridLayout.findIndex(
          l => l.id === panelRef.id
        );
        if (newLayoutIndex > -1) {
          const l = newGridLayout[newLayoutIndex];
          state.parts.panel[panelRef.id] = {
            ...state.parts.panel[panelRef.id],
            layout: {
              x: l.x,
              y: l.y,
              w: l.w,
              h: l.h,
            },
          };
        }
      });

      return PanelBankSectionConfigActions.setGridLayout(ref, prevGridLayout);
    }
    // Sorts panels within a section
    case getType(PanelBankSectionConfigActions.sortPanels): {
      const {refs: sectionRefs, panelBankConfigRef} = action.payload;
      const prevSorting: SectionPanelSorting[] = [];
      const prevPanelRefs: PanelTypes.Ref[][] = [];

      // Sort each section and capture prev state for undo.
      // Also track section __id__s so we can clear their overrides below.
      const sortedSectionIds = new Set<string>();
      sectionRefs.forEach(sectionRef => {
        const section = lookupPart(state.parts, sectionRef);
        sortedSectionIds.add(section.__id__);
        const panelRefs = section.panelRefs;
        prevPanelRefs.push(panelRefs);
        prevSorting.push(section.sorted);
        PanelBankSectionConfigActionsInternal.sortPanelsInternal(
          state,
          sectionRef,
          panelRefs
        );
      });

      // Clear placement overrides for panels in the sorted sections.
      // The user is explicitly choosing alphabetical order, so existing
      // placement overrides should not persist.
      let prevOverrides: Record<string, PanelPlacementOverride> | undefined;
      if (panelBankConfigRef) {
        const config = state.parts[panelBankConfigRef.type]?.[
          panelBankConfigRef.id
        ] as any;
        prevOverrides = config
          ? original(config)?.panelPlacementOverrides
          : undefined;
        if (prevOverrides) {
          const remaining: Record<string, PanelPlacementOverride> = {};
          for (const [key, override] of Object.entries(prevOverrides)) {
            if (!sortedSectionIds.has(override.sectionId)) {
              remaining[key] = override;
            }
          }
          config.panelPlacementOverrides =
            Object.keys(remaining).length > 0 ? remaining : undefined;
        }
      }

      return PanelBankSectionConfigActions.setSectionPanelRefsAndUndoSortingSetting(
        sectionRefs,
        prevPanelRefs,
        prevSorting,
        panelBankConfigRef,
        prevOverrides
      );
    }
    case getType(
      PanelBankSectionConfigActions.setSectionPanelRefsAndUndoSortingSetting
    ): {
      const {
        refs,
        orderedPanelRefs,
        sectionSortings,
        panelBankConfigRef: pbcRef,
        prevOverrides: overridesToRestore,
      } = action.payload;
      const prevPanelRefs: PanelTypes.Ref[][] = [];
      const prevSorting: SectionPanelSorting[] = [];
      refs.forEach((ref, index) => {
        prevPanelRefs.push(lookupPart(state.parts, ref).panelRefs);
        prevSorting.push(lookupPart(state.parts, ref).sorted);

        lookupPart(state.parts, ref).sorted = sectionSortings[index];
        lookupPart(state.parts, ref).panelRefs = orderedPanelRefs[index];
      });

      // Capture current overrides for re-redo, then restore previous overrides
      let currentOverrides: Record<string, PanelPlacementOverride> | undefined;
      if (pbcRef) {
        const config = state.parts[pbcRef.type]?.[pbcRef.id] as any;
        if (config) {
          currentOverrides = original(config)?.panelPlacementOverrides;
          config.panelPlacementOverrides = overridesToRestore;
        }
      }

      return PanelBankSectionConfigActions.setSectionPanelRefsAndUndoSortingSetting(
        refs,
        prevPanelRefs,
        prevSorting,
        pbcRef,
        currentOverrides
      );
    }
    case getType(PanelBankSectionConfigActions.insertUpdatedPanel): {
      const {ref, fromPanelRef, panelRef, panelBankConfigRef} = action.payload;
      const originalState = original(castDraft(state)) as ViewReducerState;
      const oldPanel = Normalize.denormalize(originalState.parts, panelRef);
      const editedPanel = lookupPart(originalState.parts, fromPanelRef);

      const normalizedSectionConfig: PanelBankSectionConfigNormalized =
        lookupPart(originalState.parts, ref);
      const prevIsPanelsAuto = normalizedSectionConfig.isPanelsAuto;

      // Check sparse override eligibility on the original panel (not the copy)
      const sparseEnabled = isSparseOverridesEnabled();
      const {eligible: panelEligible, panelKey} =
        ConfigOverrideUtils.isPanelEligibleForConfigOverride(
          originalState,
          panelRef,
          ref,
          panelBankConfigRef
        );

      const eligible =
        sparseEnabled &&
        panelEligible &&
        panelBankConfigRef != null &&
        panelKey != null;

      let overrideInfo:
        | {
            panelBankConfigRef: PanelBankConfigRef;
            panelKey: string;
            prevOverride: PanelConfigOverride | undefined;
          }
        | undefined;

      const updatedPanel = {
        ...editedPanel,
        // Sparse path: keep isAuto true; legacy path: set to false
        isAuto: eligible ? true : false,
        key: eligible && panelKey != null ? panelKey : editedPanel.key,
      };

      if (eligible) {
        // Store config override on the draft; capture the previous override
        // from the original state so undo can restore it (there may already
        // be an override from a prior inline setConfig edit).
        const origPbConfig = lookupPart(
          originalState.parts,
          panelBankConfigRef
        );
        const prevOverride = origPbConfig.panelConfigOverrides?.[panelKey];

        const pbConfig = lookupPart(state.parts, panelBankConfigRef);
        if (pbConfig.panelConfigOverrides == null) {
          pbConfig.panelConfigOverrides = {};
        }
        pbConfig.panelConfigOverrides[panelKey] =
          ConfigOverrideUtils.mergeConfigOverride(
            pbConfig.panelConfigOverrides[panelKey],
            updatedPanel.config
          );
        overrideInfo = {
          panelBankConfigRef,
          panelKey,
          prevOverride,
        };
      } else if (
        sparseEnabled &&
        panelBankConfigRef != null &&
        panelKey != null
      ) {
        // The edited panel is not eligible for sparse overrides, but it may
        // have been eligible before this edit. If so, there can be a stale
        // override keyed by the old auto-panel identity.
        const origPbConfig = lookupPart(
          originalState.parts,
          panelBankConfigRef
        );
        const hadOverride =
          origPbConfig.panelConfigOverrides != null &&
          Object.prototype.hasOwnProperty.call(
            origPbConfig.panelConfigOverrides,
            panelKey
          );

        if (
          hadOverride &&
          !ConfigOverrideUtils.isConfigOverrideKeyUsedByAutoPanel(
            state,
            panelBankConfigRef,
            panelKey,
            panelRef
          )
        ) {
          // Only remove the stale override if no remaining auto panel still
          // uses this key. Otherwise, another panel would lose its override.
          const pbConfig = lookupPart(state.parts, panelBankConfigRef);
          const prevOverride = origPbConfig.panelConfigOverrides![panelKey];

          if (pbConfig.panelConfigOverrides) {
            delete pbConfig.panelConfigOverrides[panelKey];
            if (Object.keys(pbConfig.panelConfigOverrides).length === 0) {
              delete pbConfig.panelConfigOverrides;
            }
          }

          overrideInfo = {
            panelBankConfigRef,
            panelKey,
            prevOverride,
          };
        }
      }

      const addedRef = Normalize.addObj(
        state.parts,
        fromPanelRef.type,
        panelRef.viewID,
        updatedPanel
      );
      if (fromPanelRef.type !== addedRef.type) {
        throw new Error('invalid action');
      }
      if (normalizedSectionConfig.sorted === SectionPanelSorting.Alphabetical) {
        movePanelAlphabeticallyInSection(
          state,
          normalizedSectionConfig,
          panelRef,
          updatedPanel
        );
      }
      replacePart(state, panelRef, addedRef);

      return PanelBankSectionConfigActions.undoInsertUpdatedPanel(
        ref,
        oldPanel,
        panelRef,
        prevIsPanelsAuto,
        overrideInfo
      );
    }
    case getType(PanelBankSectionConfigActions.undoInsertUpdatedPanel): {
      const {ref, panel, panelRef, isPanelsAuto, overrideInfo} = action.payload;
      const originalState = original(castDraft(state)) as ViewReducerState;
      const oldPanel = Normalize.denormalize(originalState.parts, panelRef);
      const normalizedSectionConfig = lookupPart(state.parts, ref);

      // revert section.isPanelsAuto
      const prevIsPanelsAuto = normalizedSectionConfig.isPanelsAuto;
      normalizedSectionConfig.isPanelsAuto = isPanelsAuto;

      // Swap config override (mirrors setConfig undo pattern)
      let prevOverrideForInverse: typeof overrideInfo = undefined;
      if (overrideInfo) {
        const {panelBankConfigRef, panelKey, prevOverride} = overrideInfo;
        const pbConfig = lookupPart(state.parts, panelBankConfigRef);

        // Capture current override for the inverse action
        const currentOverride = pbConfig.panelConfigOverrides?.[panelKey];
        prevOverrideForInverse = {
          panelBankConfigRef,
          panelKey,
          prevOverride:
            currentOverride == null
              ? currentOverride
              : original(castDraft(currentOverride)),
        };

        // Restore the previous override state
        if (prevOverride) {
          if (pbConfig.panelConfigOverrides == null) {
            pbConfig.panelConfigOverrides = {};
          }
          pbConfig.panelConfigOverrides![panelKey] = prevOverride;
        } else if (pbConfig.panelConfigOverrides) {
          delete pbConfig.panelConfigOverrides[panelKey];
          if (Object.keys(pbConfig.panelConfigOverrides).length === 0) {
            delete pbConfig.panelConfigOverrides;
          }
        }
      }

      const addedRef = Normalize.addObj(
        state.parts,
        panelRef.type,
        panelRef.viewID,
        panel
      );
      if (normalizedSectionConfig.sorted === SectionPanelSorting.Alphabetical) {
        movePanelAlphabeticallyInSection(
          state,
          normalizedSectionConfig,
          panelRef,
          panel
        );
      }
      replacePart(state, panelRef, addedRef);

      return PanelBankSectionConfigActions.undoInsertUpdatedPanel(
        ref,
        oldPanel,
        panelRef,
        prevIsPanelsAuto,
        prevOverrideForInverse
      );
    }
    case getType(ReportActions.setWidth): {
      const {ref, width} = action.payload;
      const prevWidth = lookupPart(state.parts, ref).width;
      lookupPart(state.parts, ref).width = width;
      return ReportActions.setWidth(ref, prevWidth);
    }
    case getType(ReportActions.setSpecVersion): {
      const {ref, specVersion} = action.payload;
      const prevVersion =
        lookupPart(state.parts, ref).version || ReportSpecVersion.V0;
      lookupPart(state.parts, ref).version = specVersion;
      return ReportActions.setSpecVersion(ref, prevVersion);
    }
    case getType(ReportActions.addAuthor): {
      const {ref, author} = action.payload;
      const prevAuthors = lookupPart(state.parts, ref).authors ?? [];
      if (!prevAuthors.some(a => a.username === author.username)) {
        lookupPart(state.parts, ref).authors = [...prevAuthors, author];
      }
      return ReportActions.removeAuthor(ref, author);
    }
    case getType(ReportActions.removeAuthor): {
      const {ref, author} = action.payload;
      const prevAuthors = lookupPart(state.parts, ref).authors ?? [];
      const newAuthors = prevAuthors.filter(
        a => a.username !== author.username
      );
      lookupPart(state.parts, ref).authors = newAuthors;
      return ReportActions.addAuthor(ref, author);
    }
    case getType(ReportActions.setBlocks): {
      const {ref, blocks} = action.payload;
      const prevBlocks = original(
        castDraft(lookupPart(state.parts, ref).blocks)
      )!;
      lookupPart(state.parts, ref).blocks = castDraft(blocks);
      return ReportActions.setBlocks(ref, prevBlocks);
    }
    case getType(Actions.setDescription): {
      const {ref, description} = action.payload;
      const prevDescription = state.views[ref.id].description;
      state.views[ref.id].description = description;
      return Actions.setDescription(ref, prevDescription);
    }
    case getType(Actions.setPreviewUrl): {
      const {ref, previewUrl} = action.payload;
      const prevPreviewUrl = state.views[ref.id].previewUrl;
      state.views[ref.id].previewUrl = previewUrl;
      return Actions.setPreviewUrl(ref, prevPreviewUrl);
    }
    case getType(Actions.setCoverUrl): {
      const {ref, coverUrl} = action.payload;
      const prevCoverUrl = state.views[ref.id].coverUrl;
      state.views[ref.id].coverUrl = coverUrl;
      return Actions.setCoverUrl(ref, prevCoverUrl);
    }
  }
  throw new Error('Action not undoable');
}

export function applyAndMakeInverseActionImmer(
  draft: ViewReducerState,
  action: ActionType
): ActionType {
  const inverseAction = applyAndMakeInverseActionImmerInner(draft, action);
  markSectionPanelsAutoImmer(draft, action, inverseAction);
  return inverseAction;
}

// Should be called from the main reducer for all undoable actions processed with immer
export function applyUndoableActionImmer(
  draft: ViewReducerState,
  action: ActionType
) {
  // Check if action should be undoable before computing inverse
  const payload = (action as any).payload;
  if (payload?.options?.isUndoable === false) {
    // Apply action but don't track undo/redo
    applyAndMakeInverseActionImmer(draft, action);
    return;
  }

  // Normal undoable path
  const inverseAction = applyAndMakeInverseActionImmer(draft, action);
  // Actions can be quite large - especially those that contain weave types. As
  // these build up, it results in the application's immer-based reducer to slow
  // down significantly. This is because it needs to walk the entire state tree.
  // Since undo actions are not meant to be mutated, but rather used to revert
  // the state, we can freeze them and avoid the performance hit.
  const frozenAction = freeze(inverseAction);
  draft.undoActions.push({id: ID(), action: frozenAction});
  draft.redoActions = [];
}

export function immerViewsReducer(state: ViewReducerState, action: ActionType) {
  return produce(state, (draft: ViewReducerState) => {
    switch (action.type) {
      case getType(Actions.loadMetadataListStarted): {
        draft.lists[action.payload.id] = {
          loading: true,
          query: action.payload.params,
          viewIds: [],
        };
        break;
      }

      case getType(ActionsInternal.loadMetadataListFinished): {
        const query = draft.lists[action.payload.id];
        if (!query) {
          // Happens if we unload before the metadatalist query finishes
          return;
        }
        query.loading = false;
        query.viewIds = action.payload.result.map(v => v.cid);
        for (const v of action.payload.result) {
          draft.views[v.cid] = {
            ...v,
            autoSave: false,
            saving: false,
            modified: false,
            loading: false,
            starLoading: false,
            panelCommentsEnabled: false,
          };
        }
        break;
      }

      case getType(ActionsInternal.clearReportViews): {
        // Clicking on share in a workspace panel will create a report, so
        // we only want to cleanup the old report views that aren't being used.
        for (const [reportCID, viewRef] of Object.entries(draft.views)) {
          if (viewRef.type === 'runs' || viewRef.type === 'runs/draft') {
            delete draft.views[reportCID];
          }
        }
        break;
      }

      case getType(ActionsInternal.loadStarted): {
        draft.loading = true;

        // Clear undo/redo state
        draft.redoActions = [];
        draft.undoActions = [];

        break;
      }
      case getType(ActionsInternal.unloadNoMatch): {
        draft.loading = false;

        // Clear undo/redo state
        draft.redoActions = [];
        draft.undoActions = [];

        break;
      }

      case getType(ActionsInternal.addNormalizedPanelGrid): {
        const {partsWithRefs} = action.payload;

        for (const {ref, part} of partsWithRefs) {
          draft.parts[ref.type as keyof StateType][ref.id] = part;
        }

        break;
      }

      case getType(ActionsInternal.removeNormalizedPanelGrid): {
        const {sectionRef} = action.payload;

        deleteParts(draft, sectionRef);

        break;
      }

      case getType(ActionsInternal.unloadMetadataList): {
        const viewListID = action.payload.id;
        delete draft.lists[viewListID];
        break;
      }

      case getType(ActionsInternal.unloadView): {
        const viewID = action.payload.id;
        delete draft.views[viewID];
        break;
      }

      case getType(ActionsInternal.updateViewName): {
        const {ref, name} = action.payload;
        const view = draft.views[ref.id];
        view.name = name;

        const date = new Date();
        view.updatedAt = date.toISOString();

        break;
      }

      case getType(ActionsInternal.deleteUndoRedoHistory): {
        draft.undoActions = [];
        draft.redoActions = [];
        break;
      }

      case getType(Actions.undo): {
        const undoItem = draft.undoActions.pop();
        if (undoItem != null) {
          const redoAction = applyAndMakeInverseActionImmer(
            draft,
            undoItem.action
          );
          // must preserve ID when moving between undo and redo history!
          const redoItem = {id: undoItem.id, action: redoAction};
          draft.redoActions.push(redoItem);
        }
        break;
      }

      case getType(Actions.redo): {
        const redoItem = draft.redoActions.pop();
        if (redoItem != null) {
          const undoAction = applyAndMakeInverseActionImmer(
            draft,
            redoItem.action
          );
          // must preserve ID when moving between undo and redo history!
          const undoItem = {id: redoItem.id, action: undoAction};
          draft.undoActions.push(undoItem);
        }
        break;
      }

      case getType(RunSetActions.visualizeAllIfNoneVisualized): {
        const {ref} = action.payload;
        const runSetPart = lookupPart(draft.parts, ref);
        const groupSelectionsPart = lookupPart(
          draft.parts,
          runSetPart.groupSelectionsRef
        );
        if (SM.isNoneSelected(groupSelectionsPart)) {
          SM.selectAllMutate(groupSelectionsPart);
        }
        break;
      }

      case getType(ActionsInternal.markModified): {
        draft.views[action.payload.id].modified = true;
        break;
      }

      case getType(ActionsInternal.saveStarted): {
        if (draft.views[action.payload.cid]) {
          draft.views[action.payload.cid].saving = true;
        } else {
          console.error(
            'saveStarted: undefined draft.views for payload id:',
            action?.payload?.cid
          );
        }
        break;
      }

      case getType(ActionsInternal.saveFailed): {
        // Note that the UI isn't well tested after we've
        // had a sync failure.
        const {cid} = action.payload;
        if (draft.views[cid]) {
          draft.views[cid].saving = false;
        } else {
          console.error(
            'saveFailed: undefined draft.views for payload id:',
            action?.payload?.cid
          );
        }
        break;
      }

      case getType(ActionsInternal.starViewStarted): {
        const {id} = action.payload;
        draft.views[id].starLoading = true;
        break;
      }

      case getType(ActionsInternal.starViewFinished): {
        const {id, starCount} = action.payload;
        draft.views[id].starLoading = false;
        draft.views[id].starCount = starCount;
        draft.views[id].starred = true;
        break;
      }

      case getType(ActionsInternal.unstarViewStarted): {
        const {id} = action.payload;
        draft.views[id].starLoading = true;
        break;
      }

      case getType(ActionsInternal.unstarViewFinished): {
        const {id, starCount} = action.payload;
        draft.views[id].starLoading = false;
        draft.views[id].starCount = starCount;
        draft.views[id].starred = false;
        break;
      }

      case getType(Actions.setLocked): {
        const {ref, locked} = action.payload;
        draft.views[ref.id].locked = locked;
        break;
      }

      case getType(Actions.addAccessToken): {
        const {ref, accessToken} = action.payload;
        const v = draft.views[ref.id];
        if (v.accessTokens == null) {
          v.accessTokens = [];
        }
        v.accessTokens.push(accessToken);
        break;
      }

      case getType(Actions.updateAccessToken): {
        const {ref, accessToken} = action.payload;
        const view = draft.views[ref.id];
        if (view.accessTokens == null) {
          view.accessTokens = [];
        }
        view.accessTokens = [
          ...view.accessTokens.filter(t => t.token !== accessToken.token),
          accessToken,
        ];
        break;
      }

      case getType(Actions.removeAccessToken): {
        const {ref, token} = action.payload;
        const v = draft.views[ref.id];
        if (v.accessTokens != null) {
          v.accessTokens = v.accessTokens.filter(at => at.token !== token);
        }
        break;
      }

      case getType(ReportActions.deleteDiscussionComment): {
        const {discussionThreadRef, discussionCommentRef} =
          action.payload.params;
        // Remove the comment from the thread
        const discussionThread = lookupPart(draft.parts, discussionThreadRef);
        const commentIndex = _.findIndex(
          discussionThread.commentRefs,
          discussionCommentRef
        );
        if (commentIndex === -1) {
          throw new Error('invalid action');
        }
        discussionThread.commentRefs.splice(commentIndex, 1);

        // Delete the comment
        removeHistoryForObject(draft, discussionCommentRef);
        deleteParts(draft, discussionCommentRef);
        break;
      }

      case getType(ReportActions.deleteDiscussionThread): {
        const {ref, params} = action.payload;
        const {discussionThreadRef} = params;

        // Remove the thread from the report
        const report = lookupPart(draft.parts, ref);
        const threadIndex = _.findIndex(
          report.discussionThreadRefs,
          discussionThreadRef
        );
        if (threadIndex === -1) {
          throw new Error('invalid action');
        }
        report.discussionThreadRefs.splice(threadIndex, 1);

        // Delete the thread
        removeHistoryForObject(draft, discussionThreadRef);
        deleteParts(draft, discussionThreadRef);
        break;
      }

      case getType(ReportActions.loadDiscussionThreads): {
        const {ref, response} = action.payload;
        const reportPart = lookupPart(draft.parts, ref);
        const existingThreadRefs = reportPart.discussionThreadRefs;
        const newThreadRefs = [];
        let existingThreadI = 0;
        for (const responseThread of response.discussionThreads) {
          // if the discussion thread ref already exists, don't replace it with a new one
          const existingThreadRef = existingThreadRefs[existingThreadI];
          if (existingThreadRef != null) {
            const existingThreadPart =
              draft.parts['discussion-thread'][existingThreadRef.id];
            if (existingThreadPart.id === responseThread.id) {
              const existingCommentRefs = existingThreadPart.commentRefs;
              const newCommentRefs = [];
              let existingCommentI = 0;
              for (const responseComment of responseThread.comments) {
                const existingCommentRef =
                  existingCommentRefs[existingCommentI];
                if (existingCommentRef != null) {
                  const existingCommentPart =
                    draft.parts['discussion-comment'][existingCommentRef.id];
                  if (existingCommentPart.id === responseComment.id) {
                    draft.parts['discussion-comment'][existingCommentRef.id] =
                      responseComment;
                    newCommentRefs.push(existingCommentRef);
                    existingCommentI++;
                    continue;
                  }
                }
                newCommentRefs.push(
                  Normalize.addObj(
                    draft.parts,
                    'discussion-comment',
                    ref.viewID,
                    responseComment
                  )
                );
              }

              draft.parts['discussion-thread'][existingThreadRef.id] = {
                ..._.omit(responseThread, 'comments'),
                commentRefs: newCommentRefs,
              };
              newThreadRefs.push(existingThreadRef);
              existingThreadI++;
              continue;
            }
          }
          newThreadRefs.push(
            Normalize.addObj(
              draft.parts,
              'discussion-thread',
              ref.viewID,
              responseThread
            )
          );
        }
        // Associate the threads with the report
        reportPart.discussionThreadRefs = newThreadRefs;
        break;
      }

      case getType(ReportActions.addDiscussionComment): {
        const {ref, response} = action.payload;
        let {discussionThreadRef} = action.payload;
        let isNewThread = false;
        if (discussionThreadRef == null) {
          // creating a new thread
          isNewThread = true;
          discussionThreadRef = Normalize.addObj(
            draft.parts,
            'discussion-thread',
            ref.viewID,
            response.discussionThread
          );
        }
        const newCommentRef = Normalize.addObj(
          draft.parts,
          'discussion-comment',
          ref.viewID,
          response.discussionComment
        );
        const discussionThreadPart = lookupPart(
          draft.parts,
          discussionThreadRef
        );
        // Add new comment to store
        discussionThreadPart.commentRefs.push(newCommentRef);
        // Add new thread to store
        if (isNewThread) {
          lookupPart(draft.parts, ref).discussionThreadRefs.unshift(
            discussionThreadRef
          );
        }
        // Subscribe the user to comment alerts
        draft.views[ref.viewID].alertSubscription = response.alertSubscription;
        break;
      }

      case getType(DiscussionCommentActions.updateDiscussionComment): {
        const {ref, updatedComment} = action.payload;
        draft.parts[ref.type][ref.id] = updatedComment;
        break;
      }

      case getType(Actions.setCommentAlertSubscription): {
        const {ref, subscriptionID} = action.payload;
        draft.views[ref.id].alertSubscription =
          subscriptionID == null ? undefined : {id: subscriptionID};
        break;
      }

      case getType(Actions.setAutosave): {
        const {ref, autosave} = action.payload;
        draft.views[ref.id].autoSave = autosave;
        break;
      }

      case getType(Actions.noop):
      case getType(SectionActions.setRunColor):
      case getType(SectionActions.removeRunSet):
      case getType(SectionActions.duplicateRunSet):
      case getType(SectionActions.reorderRunSet):
      case getType(SectionActions.insertRunSet):
      case getType(SectionActions.setActiveIndex):
      case getType(SectionActions.setHideRunSets):
      case getType(SectionActions.setOpen):
      case getType(SectionActions.setName):
      case getType(MarkdownBlockActions.setContent):
      case getType(MarkdownBlockActions.setCollapsed):
      case getType(PanelSettingsActions.set):
      case getType(PanelSettingsActions.update):
      case getType(RunSetActions.set):
      case getType(SortActions.set):
      case getType(SortActions.upsert):
      case getType(FilterActions.set):
      case getType(FilterActions.selectionsToFilters):
      case getType(CustomRunColorsActions.setCustomRunColor):
      case getType(CustomRunColorsActions.setCustomRunColors):
      case getType(GroupSelectionsActions.toggleExpandedRowAddress):
      case getType(GroupSelectionsActions.addBound):
      case getType(GroupSelectionsActions.selectAll):
      case getType(GroupSelectionsActions.selectNone):
      case getType(GroupSelectionsActionsInternal.setGroupSelections):
      case getType(TempSelectionsActions.selectAllVisible):
      case getType(TempSelectionsActions.selectNone):
      case getType(TempSelectionsActions.selectToggle):
      case getType(TempSelectionsActions.selectSome):
      case getType(TempSelectionsActions.selectAll):
      case getType(TempSelectionsActionsInternal.setTempSelections):
      case getType(PanelActions.setConfigs):
      case getType(PanelActions.updateConfigs):
      case getType(PanelBankConfigActions.updateSettingsAndSortPanels):
      case getType(PanelBankConfigActions.addSection):
      case getType(PanelBankConfigActions.deleteSection):
      case getType(PanelBankConfigActions.movePanelImmer):
      case getType(PanelBankConfigActions.movePanelToNewSection):
      case getType(PanelBankConfigActionsInternal.undoMovePanelImmer):
      case getType(
        PanelBankConfigActionsInternal.undoUpdateSettingsAndSortPanels
      ):
      case getType(PanelBankConfigActions.noOp):
      case getType(PanelBankConfigActions.pinSection):
      case getType(PanelBankSectionConfigActions.sortPanels):
      case getType(
        PanelBankSectionConfigActions.setSectionPanelRefsAndUndoSortingSetting
      ):
      case getType(PanelBankSectionConfigActions.insertUpdatedPanel):
      case getType(PanelBankSectionConfigActions.deletePanel):
      case getType(PanelBankSectionConfigActions.duplicatePanel):
      case getType(PanelBankSectionConfigActions.toggleType):
      case getType(PanelBankSectionConfigActions.setGridLayout):
      case getType(PanelBankSectionConfigActions.undoInsertUpdatedPanel):
      case getType(ReportActions.removeSection):
      case getType(ReportActions.setWidth):
      case getType(ReportActions.setSpecVersion):
      case getType(ReportActions.copySection):
      case getType(ReportActions.insertSection):
      case getType(ReportActions.moveSection):
      case getType(ReportActions.addAuthor):
      case getType(ReportActions.removeAuthor):
      case getType(ReportActions.setBlocks):
      case getType(Actions.rename):
      case getType(Actions.setPreviewUrl):
      case getType(Actions.setCoverUrl):
      case getType(Actions.setDescription): {
        applyUndoableActionImmer(draft, action);
        break;
      }
    }
  });
}
